import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiGet } from "@/lib/api";
import { formatMoney } from "@/lib/mockData";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DollarSign, TrendingUp, Users, ShieldAlert, CreditCard, Loader2, RefreshCw, FileSignature } from "lucide-react";

export default function Admin() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const fetchOverview = async () => {
        setLoading(true);
        try { setData(await apiGet("/admin/overview")); setErr(null); }
        catch (e) {
            setErr(e?.response?.data?.detail || "Couldn't load admin data");
            setData(null);
        } finally { setLoading(false); }
    };
    useEffect(() => { fetchOverview(); }, []);

    if (loading) {
        return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    }
    if (err) {
        return (
            <div className="px-5 sm:px-8 py-10 max-w-3xl mx-auto">
                <Card className="p-10 border-destructive/30 bg-destructive/5">
                    <ShieldAlert className="h-6 w-6 text-destructive" />
                    <h2 className="mt-4 font-serif text-2xl">Access restricted</h2>
                    <p className="mt-2 text-sm text-muted-foreground">{err}</p>
                    <p className="mt-4 text-xs text-muted-foreground">To enable admin access, set <code className="font-mono bg-secondary px-1 rounded">ADMIN_EMAIL=your@email.com</code> in <code className="font-mono bg-secondary px-1 rounded">/app/backend/.env</code> and restart backend.</p>
                </Card>
            </div>
        );
    }

    const {
        stripe_mode, revenue_total, count_paid_total, count_paid_30d, count_paid_7d,
        sessions_total, sessions_pending, users_total, users_premium,
        applications_total, applications_signed, recent_paid, daily_revenue_14d,
    } = data;

    const conversion = users_total ? Math.round(100 * users_premium / users_total) : 0;
    const signedPct = applications_total ? Math.round(100 * applications_signed / applications_total) : 0;

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-7xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Owner dashboard</p>
                    <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Revenue & metrics</h1>
                    <p className="mt-3 text-muted-foreground max-w-2xl">Everything that's been paid, everyone who's signed up, every packet signed. Source of truth for Stripe payouts: Stripe Dashboard → Payments.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`rounded-full px-3 py-1 ${stripe_mode === "LIVE" ? "bg-success/10 border-success/40 text-success" : "bg-warning/10 border-warning/40 text-warning"}`} data-testid="admin-stripe-mode">
                        Stripe · {stripe_mode}
                    </Badge>
                    <Button onClick={fetchOverview} size="sm" variant="outline" className="rounded-full" data-testid="admin-refresh-btn"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
                </div>
            </div>

            {stripe_mode === "TEST" && (
                <Card className="mt-6 p-5 border-warning/40 bg-warning/5">
                    <div className="flex items-start gap-3">
                        <ShieldAlert className="h-5 w-5 text-warning shrink-0 mt-0.5" />
                        <div>
                            <p className="font-serif text-lg">Stripe is in TEST mode — no real money is moving.</p>
                            <p className="text-sm text-muted-foreground mt-1">Replace <code className="font-mono bg-secondary px-1 rounded">STRIPE_API_KEY</code> in <code className="font-mono bg-secondary px-1 rounded">/app/backend/.env</code> with your live key (starts <code className="font-mono">sk_live_</code>) from Stripe Dashboard → Developers → API keys. Verify your bank account + identity in Stripe before going live.</p>
                        </div>
                    </div>
                </Card>
            )}

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={DollarSign} label="Revenue (all-time)" value={formatMoney(revenue_total)} sub={`${count_paid_total} paid · $5/ea`} tone="primary" testid="admin-stat-revenue" />
                <Stat icon={TrendingUp} label="Last 30 days" value={`${count_paid_30d}`} sub={`${formatMoney(count_paid_30d * 5)} collected`} tone="accent" />
                <Stat icon={Users} label="Total users" value={`${users_total}`} sub={`${users_premium} on Premium (${conversion}%)`} tone="primary" />
                <Stat icon={FileSignature} label="Signed packets" value={`${applications_signed}`} sub={`${signedPct}% of ${applications_total} drafts`} tone="accent" />
            </div>

            {/* 14-day revenue chart */}
            <Card className="mt-6 p-6 border-border">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Daily revenue</p>
                        <h2 className="font-serif text-2xl mt-1">Last 14 days</h2>
                    </div>
                    <Badge variant="outline" className="bg-secondary/60 border-border">Week-over-week: {count_paid_7d} · {formatMoney(count_paid_7d * 5)}</Badge>
                </div>
                <div className="mt-6 h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={daily_revenue_14d}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `$${v}`} />
                            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} formatter={(v) => formatMoney(v)} />
                            <Bar dataKey="revenue" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <div className="mt-6 grid lg:grid-cols-2 gap-6">
                <Card className="p-6 border-border">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Checkout funnel</p>
                    <h3 className="font-serif text-2xl mt-1">Sessions</h3>
                    <dl className="mt-5 space-y-3 text-sm">
                        <Row label="Sessions created" value={sessions_total} />
                        <Row label="Paid" value={count_paid_total} />
                        <Row label="Pending / abandoned" value={sessions_pending} />
                        <Row label="Conversion" value={`${sessions_total ? Math.round(100 * count_paid_total / sessions_total) : 0}%`} />
                    </dl>
                </Card>

                <Card className="p-6 border-border">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Recent payouts</p>
                    <h3 className="font-serif text-2xl mt-1">Last 10 paid</h3>
                    {recent_paid.length === 0 ? (
                        <p className="mt-4 text-sm text-muted-foreground italic">No paid transactions yet.</p>
                    ) : (
                        <div className="mt-4 space-y-2">
                            {recent_paid.map((r) => (
                                <div key={r.session_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/40" data-testid={`admin-tx-${r.session_id}`}>
                                    <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm truncate">{r.user_email || "—"}</p>
                                        <p className="text-[10px] font-mono text-muted-foreground truncate">{r.paid_at?.slice(0, 16) || "—"}</p>
                                    </div>
                                    <p className="font-serif text-base">{formatMoney(r.amount)}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>

            <Card className="mt-6 p-6 border-border bg-secondary/30">
                <h3 className="font-serif text-xl">Where the money actually goes</h3>
                <ol className="mt-4 space-y-2 text-sm list-decimal pl-5 text-muted-foreground leading-relaxed">
                    <li>User pays $5 on <code className="font-mono bg-card px-1 rounded">/app/premium</code> → Stripe Checkout.</li>
                    <li>Stripe settles the charge (minus 2.9% + $0.30 fee) into your Stripe balance.</li>
                    <li>Stripe automatically transfers your Stripe balance to your linked bank account on the schedule you set (default: daily, ~2 business days after).</li>
                    <li>Your app records each transaction in the <code className="font-mono bg-card px-1 rounded">payment_transactions</code> MongoDB collection; this dashboard reads from that.</li>
                </ol>
                <p className="mt-4 text-xs text-muted-foreground">Need to refund a user or dispute a chargeback? Do it in Stripe Dashboard → Payments. Owed respects refunds automatically on the next webhook delivery.</p>
            </Card>
        </div>
    );
}

function Stat({ icon: Icon, label, value, sub, tone, testid }) {
    const toneMap = { primary: "bg-primary text-primary-foreground", accent: "bg-accent text-accent-foreground" };
    return (
        <Card className="p-5 border-border" data-testid={testid}>
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${toneMap[tone]}`}><Icon className="h-4 w-4" /></span>
            <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
            <p className="font-serif text-3xl mt-1">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{sub}</p>
        </Card>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono">{value}</dd>
        </div>
    );
}
